import React, { useState, useRef } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonItem,
  IonButton,
  IonCard,
  IonModal,
  IonSkeletonText
} from "@ionic/react";
import {
  GoogleMap,
  useJsApiLoader,
  Autocomplete,
  DirectionsRenderer,
  Polyline,
  Marker,
} from "@react-google-maps/api";

import jeepneyRoutes from "../data/jeepneyRoutes.json";
import "./Explore.css";
import { environment } from "../environment/environment";
import remarkBreaks from "remark-breaks";
import ReactMarkdown from "react-markdown";
import MasskaraLoading from "../components/MasskaraLoading";
import { track } from "../analytics";

const containerStyle = { width: "100vw", height: "68vh" };
const center = { lat: 10.6765, lng: 122.9509 };
const libraries: ("places")[] = ["places"];
const premiumMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#d9edf1" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#3d5a61" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f4fbfd" }] },
  {
    featureType: "administrative",
    elementType: "geometry.stroke",
    stylers: [{ color: "#9ec8ce" }],
  },
  {
    featureType: "administrative.land_parcel",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "landscape.man_made",
    elementType: "geometry",
    stylers: [{ color: "#d8ece8" }],
  },
  {
    featureType: "landscape.natural",
    elementType: "geometry",
    stylers: [{ color: "#dcefe4" }],
  },
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi",
    elementType: "geometry",
    stylers: [{ color: "#dcebe8" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#f8fcfd" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#bfd8de" }],
  },
  {
    featureType: "road.arterial",
    elementType: "labels.text.fill",
    stylers: [{ color: "#54727a" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#cfe4ea" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#a9c8cf" }],
  },
  {
    featureType: "transit",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#a9d8ea" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#447184" }],
  },
];
const premiumMapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  clickableIcons: false,
  keyboardShortcuts: false,
  gestureHandling: "greedy",
  mapTypeControl: false,
  fullscreenControl: false,
  streetViewControl: false,
  styles: premiumMapStyles,
};

const bacolodBounds = {
  north: 10.85,
  south: 10.55,
  east: 123.1,
  west: 122.9,
};

// --- helpers ---
function normalizeCoords(geometry: any): google.maps.LatLngLiteral[] {
  return geometry.coordinates.map(([lng, lat]: number[]) => ({ lat, lng }));
}

function getNearestPoint(
  routeCoords: google.maps.LatLngLiteral[],
  point: google.maps.LatLngLiteral
) {
  let nearest = routeCoords[0];
  let minDist = Number.MAX_VALUE;
  let idx = 0;

  routeCoords.forEach((coord, i) => {
    const dist = metersBetween(point, coord);
    if (dist < minDist) {
      minDist = dist;
      nearest = coord;
      idx = i;
    }
  });

  return { coord: nearest, index: idx, dist: minDist };
}

function sliceSegmentOneWay(
  coords: google.maps.LatLngLiteral[],
  startIdx: number,
  endIdx: number
): google.maps.LatLngLiteral[] | null {
  if (startIdx <= endIdx) return coords.slice(startIdx, endIdx + 1);
  return null;
}

function metersBetween(a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral) {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// 🚀 helper: compute jeepney distance properly
function segmentDistance(seg: google.maps.LatLngLiteral[]): number {
  if (!seg || seg.length < 2) return 0;
  return seg.reduce(
    (acc, _: any, i, arr) =>
      i === 0 ? acc : acc + metersBetween(arr[i - 1], arr[i]),
    0
  );
}

// 🚀 helper: convert distance (m) to travel time (s)
function travelTime(dist: number, mode: "walk" | "jeepney"): number {
  return mode === "walk" ? dist / 1 : dist / 12;
}

function getWalkStepMainLabel(distanceMeters: number) {
  return distanceMeters > 1000 ? "Walk or ride tricycle" : "Walk";
}

function hexToRgba(hex: string, alpha: number) {
  const clean = hex.replace("#", "");
  const value = clean.length === 3
    ? clean.split("").map((char) => char + char).join("")
    : clean;

  const num = parseInt(value, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const Explore: React.FC = () => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: environment.GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [walkToJeepney, setWalkToJeepney] = useState<any>(null);
  const [walkFromJeepney, setWalkFromJeepney] = useState<any>(null);
  const [transferWalk, setTransferWalk] = useState<any>(null);
  const [steps, setSteps] = useState<any[]>([]);
  const [jeepneySegmentA, setJeepneySegmentA] = useState<
    google.maps.LatLngLiteral[]
  >([]);
  const [jeepneySegmentB, setJeepneySegmentB] = useState<
    google.maps.LatLngLiteral[]
  >([]);
  const [hasRoute, setHasRoute] = useState(false);

  const originAutocomplete = useRef<google.maps.places.Autocomplete | null>(null);
  const destinationAutocomplete =
    useRef<google.maps.places.Autocomplete | null>(null);
  const [originText, setOriginText] = useState("");
  const [destinationText, setDestinationText] = useState("");
  const [activeInput, setActiveInput] = useState<"origin" | "destination" | null>(null);
    
  const [fullRouteA, setFullRouteA] = useState<google.maps.LatLngLiteral[]>([]);
  const [fullRouteB, setFullRouteB] = useState<google.maps.LatLngLiteral[]>([]);

  const [topCandidates, setTopCandidates] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);

  const [aiAnswer, setAiAnswer] = useState<string>(""); 
  const [loadingAI, setLoadingAI] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [directRoute, setDirectRoute] = useState<google.maps.DirectionsResult | null>(null)

  const syncInputValue = (
    inputId: "origin" | "destination",
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const input = document.getElementById(inputId) as HTMLInputElement | null;
    setter(input?.value ?? "");
  };

  const clearRoute = () => {
    if (aiAnswer) {
      (document.getElementById("origin") as HTMLInputElement).value = "";
      (document.getElementById("destination") as HTMLInputElement).value = "";
      setOriginText("");
      setDestinationText("");
      setActiveInput(null);
      setAiAnswer(""); // ✅ reset AI chat
      setDirectRoute(null); // ✅ clear driving route
    } else {
      window.location.reload();
    }
    setHasRoute(false);

  };

  const showDirectRoute = async (origin: string, destination: string) => {
    if (!origin || !destination) return;
    if (!map) return;
  
    const directionsService = new google.maps.DirectionsService();
  
    directionsService.route(
      {
        origin,
        destination,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK" && result) {
          setDirectRoute(result);
  
          // ✅ auto-fit map
          const bounds = new google.maps.LatLngBounds();
          result.routes[0].overview_path.forEach((p) => bounds.extend(p));
          map.fitBounds(bounds);
        } else {
          console.error("Failed to load direct route:", status);
        }
      }
    );
  };

  const calculateRoute = async () => {
    if (!isLoaded || loadError) return;

    setShowLoading(true);

    const directionsService = new google.maps.DirectionsService();
    // small in-memory cache to avoid duplicate Directions queries during one run
    const walkCache = new Map<string, { distance: number; duration: number; result: google.maps.DirectionsResult | null }>();

    async function getWalkingRoute(o: google.maps.LatLngLiteral, d: google.maps.LatLngLiteral) {
      const key = `${o.lat},${o.lng}->${d.lat},${d.lng}`;
      if (walkCache.has(key)) return walkCache.get(key)!;

      return new Promise<{ distance: number; duration: number; result: google.maps.DirectionsResult | null }>((resolve) => {
        directionsService.route(
          { origin: o, destination: d, travelMode: google.maps.TravelMode.WALKING },
          (res, status) => {
            if (status === "OK" && res && res.routes && res.routes[0] && res.routes[0].legs && res.routes[0].legs[0]) {
              const leg = res.routes[0].legs[0];
              const out = { distance: leg.distance?.value ?? metersBetween(o, d), duration: leg.duration?.value ?? (metersBetween(o, d) / 1), result: res };
              walkCache.set(key, out);
              resolve(out);
            } else {
              // fallback: straight-line estimate
              const m = metersBetween(o, d);
              const out = { distance: m, duration: m / 1, result: null };
              walkCache.set(key, out);
              // log status to console for debugging
              console.warn("Walking directions failed:", status, "for", o, d);
              resolve(out);
            }
          }
        );
      });
    }

    if (!originAutocomplete.current || !destinationAutocomplete.current) {
      alert("Please select valid places.");
      return;
    }

    const originPlace = originAutocomplete.current.getPlace();
    const destinationPlace = destinationAutocomplete.current.getPlace();

    if (!originPlace?.geometry?.location || !destinationPlace?.geometry?.location) {
      alert("Please select valid places.");
      return;
    }

    track("navigate_route_planned", {
      origin: originPlace.name || originPlace.formatted_address || "",
      destination: destinationPlace.name || destinationPlace.formatted_address || "",
    });

    const origin = {
      lat: originPlace.geometry.location.lat(),
      lng: originPlace.geometry.location.lng(),
    };
    const destination = {
      lat: destinationPlace.geometry.location.lat(),
      lng: destinationPlace.geometry.location.lng(),
    };

    // ✅ reset before new calculation
    setWalkToJeepney(null);
    setWalkFromJeepney(null);
    setTransferWalk(null);
    setJeepneySegmentA([]);
    setJeepneySegmentB([]);
    setSteps([]);

    // Scenario 0 — actual walking distance
    const directWalkDist = metersBetween(origin, destination);
    if (directWalkDist < 300) {
      const actualWalk = await getWalkingRoute(origin, destination);
      // show the walking path result if Google returned a DirectionsResult
      if (actualWalk.result) setWalkToJeepney(actualWalk.result);
      else {
        // fallback: call route once (rare)
        directionsService.route(
          { origin, destination, travelMode: google.maps.TravelMode.WALKING },
          (res, status) => status === "OK" && res && setWalkToJeepney(res)
        );
      }

      setSteps([{
        type: "walk",
        mainLabel: getWalkStepMainLabel(actualWalk.distance),
        label: ` to destination`,
        color: "#093843",
        distance: actualWalk.distance.toFixed(0) + (actualWalk.distance === 0 ? " meter":" meters")
      }]);
      setHasRoute(true);
      return;
    }

    // ---------------------------------------------------------
    // 1. Find the 10 nearest jeepney routes to the origin
    // ---------------------------------------------------------
    const nearest = jeepneyRoutes.features
      .map((feat: any, i: number) => {
        const coords = normalizeCoords(feat.geometry);
        const nO = getNearestPoint(coords, origin);
        return { index: i, dist: nO.dist, nearestOrigin: nO };
      })
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 12);

    if (nearest.length === 0) {
      alert("No jeepney routes found.");
      return;
    }

    // ---------------------------------------------------------
    // 2. Helper to test scenarios for one jeepney candidate
    // ---------------------------------------------------------
    const analyzeRoute = (routeIdx: number, nOrigin: any) => {
      const candidates: any[] = [];
    
      const firstRoute = jeepneyRoutes.features[routeIdx];
      const coordsA = normalizeCoords(firstRoute.geometry);
    
      if (nOrigin.dist > 5000) return candidates;
        // Track if we already found a good direct option
      let hasGoodDirect = false;
    
      // --- Scenario 1 ---
      const nDestA = getNearestPoint(coordsA, destination);
      const segA = sliceSegmentOneWay(coordsA, nOrigin.index, nDestA.index);
      if (segA) {
        const walkDist = metersBetween(nDestA.coord, destination);
        if (walkDist <= 3000) { // accept both short & long walks
          const type = walkDist <= 300 ? "scenario1" : "scenario1-walklong";
          candidates.push({
            ok: true,
            type,
            segA,
            walkTo: { origin, dest: nOrigin.coord },
            walkFrom: { origin: nDestA.coord, dest: destination },
            transfer: null,
            totalDist: walkDist + nOrigin.dist + segmentDistance(segA),
            totalTime:
              travelTime(nOrigin.dist, "walk") +
              travelTime(segmentDistance(segA), "jeepney") +
              travelTime(walkDist, "walk"),
            routeIdx,
          });
          if (type === "scenario1") {
            hasGoodDirect = true;
          }
        }
      }
    
      // --- Scenario 2 ---
      for (let f2 = 0; f2 < jeepneyRoutes.features.length; f2++) {
        if (f2 === routeIdx) continue;
        const featB = jeepneyRoutes.features[f2];
        if (featB.properties.name !== firstRoute.properties.name) continue;

        const coordsB = normalizeCoords(featB.geometry);
        const nDestB = getNearestPoint(coordsB, destination);
        const segB = sliceSegmentOneWay(coordsB, 0, nDestB.index);
        if (segB) {
          const combined = [...coordsA.slice(nOrigin.index), ...segB];
          const rideDist = segmentDistance(combined);
          if (rideDist < 250) continue; // skip if ride is short
          const walkDist = metersBetween(nDestB.coord, destination);
          if (walkDist <= 3000) {
            const type = walkDist <= 300 ? "scenario2" : "scenario2-walklong";
            candidates.push({
              ok: true,
              type,
              segA: combined,
              walkTo: { origin, dest: nOrigin.coord },
              walkFrom: { origin: nDestB.coord, dest: destination },
              transfer: null,
              totalDist: walkDist + nOrigin.dist + segmentDistance(combined),
              totalTime:
                travelTime(nOrigin.dist, "walk") +
                travelTime(segmentDistance(combined), "jeepney") +
                travelTime(walkDist, "walk"),
              routeIdx,
            });
            if (type === "scenario2") {
              hasGoodDirect = true;
            }
          }
        }
      }

      // --- Scenario 3 ---
      if (!hasGoodDirect) {
        for (let f2 = 0; f2 < jeepneyRoutes.features.length; f2++) {
          if (f2 === routeIdx) continue;
          const featB = jeepneyRoutes.features[f2];
          if (featB.properties.name === firstRoute.properties.name) continue;

          const coordsB = normalizeCoords(featB.geometry);
          const nDestB = getNearestPoint(coordsB, destination);

          // check transfer
          let bestTransfer = { dist: Number.MAX_VALUE, tA: null as any, tB: null as any };
          coordsA.forEach((cA, iA) => {
            coordsB.forEach((cB, iB) => {
              const d = metersBetween(cA, cB);
              if (d < bestTransfer.dist) {
                bestTransfer = { dist: d, tA: { coord: cA, index: iA }, tB: { coord: cB, index: iB } };
              }
            });
          });

          if (!bestTransfer.tA || !bestTransfer.tB || bestTransfer.dist > 600) continue;

          const segA = sliceSegmentOneWay(coordsA, nOrigin.index, bestTransfer.tA.index);
          const segB = sliceSegmentOneWay(coordsB, bestTransfer.tB.index, nDestB.index);

          if (!segA || !segB) continue;

          const rideDistA = segmentDistance(segA);
          const rideDistB = segmentDistance(segB);
          // 🚫 Skip if jeepney ride is too short (< 250m)
          if (rideDistA < 250) continue;
          if (rideDistB < 250) continue;

          const walkDist = metersBetween(nDestB.coord, destination);
          if (walkDist > 3000) continue;

          candidates.push({
            ok: true,
            type: "scenario3",
            segA,
            segB,
            walkTo: { origin, dest: nOrigin.coord },
            walkFrom: { origin: nDestB.coord, dest: destination },
            transfer: { origin: bestTransfer.tA.coord, dest: bestTransfer.tB.coord },
            totalDist: walkDist + nOrigin.dist + bestTransfer.dist + segmentDistance(segA) + segmentDistance(segB),
            totalTime:
              travelTime(nOrigin.dist, "walk") +
              travelTime(segmentDistance(segA), "jeepney") +
              travelTime(bestTransfer.dist, "walk") +
              travelTime(segmentDistance(segB), "jeepney") +
              travelTime(walkDist, "walk"),
            routeIdx,
            segBRouteIdx: f2,
          });
        }
      }
      return candidates;
    };

    // --------------------------------
    // Helper: Scenario 3 transfer
    // --------------------------------
    const analyzeTransfer = (
      pathA: google.maps.LatLngLiteral[],
      originIndex: number,
      routeIdx: number,
      nOrigin: any,
      destination: google.maps.LatLngLiteral
    ) => {
      let bestTransferResult: any = null;

      for (let f2 = 0; f2 < jeepneyRoutes.features.length; f2++) {
        if (f2 === routeIdx) continue;

        const featB = jeepneyRoutes.features[f2];
        if (featB.properties.name === jeepneyRoutes.features[routeIdx].properties.name) continue;

        const coordsB = normalizeCoords(featB.geometry);
        const nDestB = getNearestPoint(coordsB, destination);

        // test every possible transfer point pair
        pathA.forEach((cA, iA) => {
          coordsB.forEach((cB, iB) => {
            const transferWalkDist = metersBetween(cA, cB);
            if (transferWalkDist > 600) return; // limit unrealistic transfers

            const segA = sliceSegmentOneWay(pathA, originIndex, iA);
            const segB = sliceSegmentOneWay(coordsB, iB, nDestB.index);
            if (!segA || !segB) return;

            const walkToDest = metersBetween(nDestB.coord, destination);
            if (walkToDest > 300) return; // skip bad routes

            const rideDistA = segmentDistance(segA);
            const rideDistB = segmentDistance(segB);
            if (rideDistA < 250 || rideDistB < 250) return; // skip if ride is short

            // compute total travel time
            const totalTime =
              travelTime(nOrigin.dist, "walk") +
              travelTime(segmentDistance(segA), "jeepney") +
              travelTime(transferWalkDist, "walk") +
              travelTime(segmentDistance(segB), "jeepney") +
              travelTime(walkToDest, "walk");

            // keep best transfer option
            if (!bestTransferResult || totalTime < bestTransferResult.totalTime) {
              bestTransferResult = {
                ok: true,
                type: "scenario3-1-2",
                segA,
                segB,
                walkTo: { origin, dest: pathA[originIndex] },
                transfer: { origin: cA, dest: cB },
                walkFrom: { origin: nDestB.coord, dest: destination },
                totalTime,
                routeIdx,                 // first jeepney
                segBRouteIdx: f2,         // second jeepney
                segBRouteName: featB.properties.name,
              };
            }
          });
        });
      }

      return bestTransferResult;
    };

    // Helper
    const buildSteps = async (route: any, routeIdx: number) => {
      const steps: any[] = [];

      if (route.walkTo) {
        const actualWalk = await getWalkingRoute(route.walkTo.origin, route.walkTo.dest);
        steps.push({
          type: "walk",
          mainLabel: getWalkStepMainLabel(
            actualWalk.distance ?? metersBetween(route.walkTo.origin, route.walkTo.dest)
          ),
          label: ` to jeepney route`,
          color: "#093843",
          distance: ((actualWalk.distance ?? metersBetween(route.walkTo.origin, route.walkTo.dest))/1000).toFixed(1) + " km",
        });
      }

      if (route.segA) {
        steps.push({
          type: "jeepney",
          mainLabel: "Ride",
          label: ` "${jeepneyRoutes.features[routeIdx]?.properties.name}" jeepney`,
          color: "#43ae91",
          distance: (route.segA.reduce((acc: number, _: any, i: number, arr: any[]) =>
            i === 0 ? acc : acc + metersBetween(arr[i - 1], arr[i]), 0)/1000).toFixed(1) + " km",
        });
      }

      if (route.transfer) {
        steps.push({
          type: "transfer",
          mainLabel: "Transfer",
          label: ` to another jeepney`,
          color: "#093843",
          distance: (metersBetween(route.transfer.origin, route.transfer.dest)/1000).toFixed(1) + " km",
        });
      }

      if (route.segB) {
        steps.push({
          type: "jeepney",
          mainLabel: "Ride",
          label: ` "${route.segBRouteName ?? jeepneyRoutes.features[route.segBRouteIdx].properties.name}" jeepney`,
          color: "#ff4570",
          distance: (route.segB.reduce((acc: number, _: any, i: number, arr: any[]) =>
            i === 0 ? acc : acc + metersBetween(arr[i - 1], arr[i]), 0)/1000).toFixed(1) + " km",
        });
      }

      if (route.walkFrom) {
        const actualWalk = await getWalkingRoute(route.walkFrom.origin, route.walkFrom.dest);
        steps.push({
          type: "walk",
          mainLabel: getWalkStepMainLabel(
            actualWalk.distance ?? metersBetween(route.walkFrom.origin, route.walkFrom.dest)
          ),
          label: ` to destination`,
          color: "#093843",
          distance: ((actualWalk.distance ?? metersBetween(route.walkFrom.origin, route.walkFrom.dest))/1000).toFixed(1) + " km",
        });
      }

      return steps;
    };

    // ---------------------------------------------------------
    // 3. Run scenarios for each candidate & pick best
    // ---------------------------------------------------------
    const candidates: any[] = [];
    nearest.forEach((n) => {
      candidates.push(...analyzeRoute(n.index, n.nearestOrigin));
    });

    // TODO - comment every release
    console.table(
      candidates.map(c => ({
        route: c.routeIdx,
        jeepney: jeepneyRoutes.features[c.routeIdx]?.properties?.name ?? "Unknown",
        jeepney2: jeepneyRoutes.features[c.segBRouteIdx]?.properties?.name ?? "Unknown",
        type: c.type,
        time: c.totalTime?.toFixed(0) + " s",
        dist: c.totalDist + " m"
      }))
    );

    // pick best globally
    if (candidates.length === 0) {
      setLoadingAI(true)
      // alert("No jeepney route found under conditions.");
      const originAdr = (document.getElementById("origin") as HTMLInputElement)?.value;
      const destinationAdr = (document.getElementById("destination") as HTMLInputElement)?.value;
      
      console.log("User typed origin:", originAdr);
      console.log("User typed destination:", destinationAdr);
      const res = await fetch("https://asktransport-lrenkrr2bq-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin: originAdr, destination: destinationAdr }),
      });
      const data = await res.json();
      console.log(data.answer);
      setLoadingAI(false)
      setAiAnswer(data.answer); // ✅ Save AI answer
      setHasRoute(true);        // so button changes to Clear
      setShowLoading(false);
      // 🗺️ draw driving route from origin to destination
      showDirectRoute(originAdr, destinationAdr);
      
      return;
    }
    // const best = candidates.sort((a, b) => a.totalTime - b.totalTime)[0];

    // 🚏 preferred order of scenarios
    const scenarioPriority: Record<string, number> = {
      "scenario0": 0,
      "scenario2": 1,
      "scenario1": 2,
      "scenario2-walklong": 3,
      "scenario1-walklong": 4,
      "scenario3": 5,
    };

    const sorted = candidates.sort((a, b) => {
      // first by time
      const timeDiff = a.totalTime - b.totalTime;
      if (Math.abs(timeDiff) > 20) { // allow small wiggle room (20s)
        return timeDiff;
      }
      // then by scenario preference
      return (scenarioPriority[a.type] ?? 99) - (scenarioPriority[b.type] ?? 99);
    });
    const best = sorted[0];

    if (sorted.length > 0) {
      const within50s = sorted.filter(c => c.totalTime - best.totalTime <= 50);
      // ✅ remove duplicates by jeepney name(s)
      const uniqueCandidates = within50s.filter((candidate, index, self) => {
        // build a "key" string to identify uniqueness
        const key = candidate.segB
          ? `${jeepneyRoutes.features[candidate.routeIdx]?.properties?.name}-${jeepneyRoutes.features[candidate.segBRouteIdx]?.properties?.name}`
          : `${jeepneyRoutes.features[candidate.routeIdx]?.properties?.name}`;

        // keep only the first occurrence of this key
        return (
          index ===
          self.findIndex((c) => {
            const compareKey = c.segB
              ? `${jeepneyRoutes.features[c.routeIdx]?.properties?.name}-${jeepneyRoutes.features[c.segBRouteIdx]?.properties?.name}`
              : `${jeepneyRoutes.features[c.routeIdx]?.properties?.name}`;
            return compareKey === key;
          })
        );
      });
      // limit to 5
      setTopCandidates(uniqueCandidates.slice(0, 5));
    }

    if (best.segA) {
      setJeepneySegmentA(best.segA);
      const coordsA = normalizeCoords(jeepneyRoutes.features[best.routeIdx].geometry);
      setFullRouteA(coordsA);
    }
    
    if (best.segB) {
      setJeepneySegmentB(best.segB);
      const coordsB = normalizeCoords(
        jeepneyRoutes.features[best.segBRouteIdx ?? best.routeIdx].geometry
      );
      setFullRouteB(coordsB);
    }

    const steps = buildSteps(best, best.routeIdx);
    setSteps(await steps);

    setHasRoute(true);
    setShowLoading(false);

    // ---------------------------------------------------------
    // 4. Render best result
    // ---------------------------------------------------------
    if (best.segA) setJeepneySegmentA(best.segA);
    if (best.segB) setJeepneySegmentB(best.segB);

    if (best.walkTo) {
      directionsService.route(
        { origin: best.walkTo.origin, destination: best.walkTo.dest, travelMode: google.maps.TravelMode.WALKING },
        (res, status) => status === "OK" && res && setWalkToJeepney(res)
      );
    }
    if (best.transfer) {
      directionsService.route(
        { origin: best.transfer.origin, destination: best.transfer.dest, travelMode: google.maps.TravelMode.WALKING },
        (res, status) => status === "OK" && res && setTransferWalk(res)
      );
    }
    if (best.walkFrom) {
      directionsService.route(
        { origin: best.walkFrom.origin, destination: best.walkFrom.dest, travelMode: google.maps.TravelMode.WALKING },
        (res, status) => status === "OK" && res && setWalkFromJeepney(res)
      );
    }

    // ✅ auto-fit map
    if (map) {
      const bounds = new google.maps.LatLngBounds();
      if (best.segA) best.segA.forEach((p: google.maps.LatLngLiteral) => bounds.extend(p));
      if (best.segB) best.segB.forEach((p: google.maps.LatLngLiteral) => bounds.extend(p));

      [walkToJeepney, transferWalk, walkFromJeepney].forEach(route => {
        if (route) {
          route.routes[0].overview_path.forEach((p: google.maps.LatLng) => bounds.extend(p));
        }
      });

      map.fitBounds(bounds);
    }
  };

  return (
    <IonPage>
      <IonContent className="content navigate-content" fullscreen>
        {loadError ? (
          <div className="map-loading-state">Failed to load Google Maps.</div>
        ) : !isLoaded ? (
          <div className="map-loading-state">Loading map...</div>
        ) : (
          <>
          <div className="navigate-panel">
          <div className="navigate-panel-card">
          <div className="navigate-panel-head">
            <h2 className="navigate-heading">Navigate Bacolod</h2>
            <p className="navigate-subcopy">
              Jeepney routes
            </p>
          </div>
          <div className="upper-inputs">
            <div className="navigate-field">
              <span className="navigate-field-label">From</span>
              <IonItem className="navigate-field-item" lines="none">
              <Autocomplete
                options={{ bounds: bacolodBounds, strictBounds: false, componentRestrictions: { country: "ph" } }}
                onLoad={(ac) => (originAutocomplete.current = ac)}
                onPlaceChanged={() => syncInputValue("origin", setOriginText)}
              >
                <div className={`autocomplete-shell${originText && activeInput !== "origin" ? " has-preview" : ""}`}>
                  <input
                    id="origin"
                    className="autocomplete-input"
                    type="text"
                    placeholder="Starting point"
                    onInput={(e) => setOriginText((e.target as HTMLInputElement).value)}
                    onFocus={() => setActiveInput("origin")}
                    onBlur={() => setActiveInput(null)}
                  />
                  {originText && activeInput !== "origin" && (
                    <div className="autocomplete-preview" aria-hidden="true">
                      {originText}
                    </div>
                  )}
                </div>
              </Autocomplete>
              </IonItem>
            </div>

            <div className="navigate-field">
              <span className="navigate-field-label">To</span>
              <IonItem className="navigate-field-item" lines="none">
              <Autocomplete
                options={{ bounds: bacolodBounds, strictBounds: false, componentRestrictions: { country: "ph" } }}
                onLoad={(ac) => (destinationAutocomplete.current = ac)}
                onPlaceChanged={() => syncInputValue("destination", setDestinationText)}
              >
              <div className={`autocomplete-shell${destinationText && activeInput !== "destination" ? " has-preview" : ""}`}>
                <input
                  id="destination"
                  className="autocomplete-input"
                  type="text"
                  placeholder="Where are you going?"
                  onInput={(e) => setDestinationText((e.target as HTMLInputElement).value)}
                  onFocus={() => setActiveInput("destination")}
                  onBlur={() => setActiveInput(null)}
                />
                {destinationText && activeInput !== "destination" && (
                  <div className="autocomplete-preview" aria-hidden="true">
                    {destinationText}
                  </div>
                )}
              </div>
              </Autocomplete>
              </IonItem>
            </div>

            <div className="navigate-actions">
            <IonButton
              mode="ios"
              className={hasRoute ? "clear" : "get-directions"}
              fill="outline"
              expand="block"
              disabled={loadingAI || !isLoaded}
              onClick={hasRoute ? clearRoute : calculateRoute}
            >
              {hasRoute ? "Clear" : "Get Directions"}
            </IonButton>
            </div>

            {loadingAI && (
            <div className="thinking-skeleton">
              <div style={{ marginTop: "12px", textAlign: "center" }}>
                <span className="thinking-text">Thinking...</span>
              </div>
              <IonSkeletonText animated style={{ width: "100%", height: "1em", borderRadius: "12px", marginBottom:"0.5em" }} />
              <IonSkeletonText animated style={{ width: "100%", height: "1em", borderRadius: "12px", marginBottom:"0.5em" }} />
              <IonSkeletonText animated style={{ width: "100%", height: "1em", borderRadius: "12px" }} />
            </div>
            )}
          </div>
          </div>

          <div className="navigate-results">
          {aiAnswer && (
            <div className="ai-answer-container">
              <IonCard className="ai-answer-card">
                  <div className="ai-chat-bubble">
                  <h5 className="title">AI Travel Guide</h5>
                  <ReactMarkdown remarkPlugins={[remarkBreaks]}>{aiAnswer}</ReactMarkdown>
                  {/* <p>{aiAnswer}</p> */}
                </div>
              </IonCard>
            </div>
          )}

          {steps && steps.length > 0 && (
            <div className="steps-container">
              {steps.map((step, i) => (
                <IonCard
                  className="direction-card"
                  key={i}
                  style={{
                    "--step-accent": step.color,
                    "--step-accent-soft": hexToRgba(step.color, 0.34),
                    "--step-accent-line": hexToRgba(step.color, 0.42),
                    "--step-surface-top": hexToRgba(step.color, 0.56),
                    "--step-surface-bottom": hexToRgba(step.color, 0.4),
                  } as React.CSSProperties}
                >
                  <span className="step-number">{i + 1}</span><span className="direction-label"><span className="direction-main-label">{step.mainLabel}</span>{step.label}</span><span className="direction-distance">{step.distance}</span>
                </IonCard>
              ))}
            </div>
          )}
          {hasRoute && topCandidates.length > 1 && (
            <div className="navigate-secondary-action">
            <IonButton
              className="btn-other-routes"
              mode="ios"
              fill="outline"
              onClick={() => {
                setShowModal(true);
              }}
            >
              See Other Jeepney Routes
            </IonButton>
            </div>
          )}
          </div>
          </div>
          <IonModal mode="ios" className="modal-other-routes" isOpen={showModal} onDidDismiss={() => setShowModal(false)}>
            <IonHeader className="header-other-routes">
              <IonToolbar className="toolbar-other-routes">
                <IonTitle className="label-modal-title">Jeepney Routes</IonTitle>
                <IonButton className="btn-modal-close" fill="clear" slot="end" onClick={() => setShowModal(false)}>Done</IonButton>
              </IonToolbar>
            </IonHeader>

            <IonContent className="content-other-routes">
            <div className="other-routes-shell">
              <div className="other-routes-intro">
                <span className="other-routes-eyebrow">{topCandidates.length} route options</span>
                <h3 className="other-routes-heading">Choose another jeepney flow</h3>
                <p className="other-routes-copy">Compare alternate ride combinations and pick the route that fits your trip best.</p>
              </div>

              <div className="other-routes-list">
              {topCandidates.map((c, i) => {
                const first = jeepneyRoutes.features[c.routeIdx]?.properties.name ?? "Unknown";
                const second = c.segBRouteName ?? (c.segBRouteIdx !== undefined ? jeepneyRoutes.features[c.segBRouteIdx]?.properties.name : null);

                return (
                  <IonCard className="jeepney-options" key={i}>
                    <div className="jeepney-option-top">
                      <strong className="card-option-label">Option {i + 1}</strong>
                      <span className="card-option-type">{second ? "2 rides" : "Direct"}</span>
                    </div>

                    <div className="jeepney-route-track">
                      <span className="card-route-pill card-route-pill-primary">{first}</span>
                      {second && (
                        <>
                          <span className="card-route-arrow">Transfer</span>
                          <span className="card-route-pill card-route-pill-secondary">{second}</span>
                        </>
                      )}
                    </div>
                  </IonCard>
                );
              })}
              </div>
            </div>
            </IonContent>
          </IonModal>
          <div className="navigate-map">
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={12}
            options={premiumMapOptions}
            onLoad={(map) => {
              setMap(map);
              // On desktop the full-bleed map container settles after mount;
              // Google Maps caches its size at init and renders blank tiles
              // until it re-measures. A real window resize event forces that;
              // pulse a few times to catch the settled size.
              let ticks = 0;
              const id = window.setInterval(() => {
                window.dispatchEvent(new Event("resize"));
                if (++ticks >= 6) {
                  window.clearInterval(id);
                  map.setCenter(center);
                }
              }, 350);
            }}
          >
            {/* 🗺️ Direct Route (AI Fallback) */}
            {directRoute && (
              <DirectionsRenderer
                directions={directRoute}
                options={{
                  polylineOptions: { strokeColor: "#007AFF", strokeWeight: 5 },
                  suppressMarkers: false,
                  preserveViewport: true,
                }}
              />
            )}

            {/* Walk to Jeepney */}
            {walkToJeepney && (
              <DirectionsRenderer
                directions={walkToJeepney}
                options={{
                  polylineOptions: { strokeColor: "#093843", strokeWeight: 3 }, suppressMarkers: true,
                  preserveViewport: true
                }}
              />
            )}
            {walkToJeepney && (
              <>
                <Marker
                  position={walkToJeepney.routes[0].legs[0].start_location}
                  label={{ text: "A", fontSize: "1.5em", fontWeight: "bold", color: "#ffffff" }}
                />
                <Marker
                  position={walkToJeepney.routes[0].legs[0].end_location}
                  label={{ text: "B", fontSize: "1.5em", fontWeight: "bold", color: "#ffffff" }}
                />
              </>
            )}

            {/* Jeepney Segment A */}
            {fullRouteA.length > 0 && (
              <Polyline
                path={fullRouteA}
                options={{
                  strokeColor: "#43ae91",
                  strokeWeight: 2,
                  strokeOpacity: 0.5,
                  zIndex: 1,
                }}
              />
            )}
            {jeepneySegmentA.length > 0 && (
              <>
                <Polyline path={jeepneySegmentA} options={{ strokeColor: "#43ae91", strokeWeight: 5 }} />
                <Marker
                  position={jeepneySegmentA[jeepneySegmentA.length - 1]}
                  label={{ text: "C", fontSize: "1.5em", fontWeight: "bold", color: "#ffffff" }}
                />
              </>
            )}

            {/* Transfer Walk */}
            {transferWalk && (
              <>
                <DirectionsRenderer
                  directions={transferWalk}
                  options={{
                    polylineOptions: {
                      strokeColor: "#093843",
                      strokeWeight: 3,
                      strokeOpacity: 0.6,
                      zIndex: 10,
                    },
                    suppressMarkers: true,
                    preserveViewport: true
                  }}
                />
                <Marker
                  position={transferWalk.routes[0].legs[0].end_location}
                  label={{ text: "D", fontSize: "1.5em", fontWeight: "bold", color: "#ffffff" }}
                />
              </>
            )}

            {/* Jeepney Segment B */}
            {fullRouteB.length > 0 && (
              <Polyline
                path={fullRouteB}
                options={{
                  strokeColor: "#ff4570",
                  strokeWeight: 2,
                  strokeOpacity: 0.2,
                  zIndex: 1,
                }}
              />
            )}
            {jeepneySegmentB.length > 0 && (
              <>
                <Polyline path={jeepneySegmentB} options={{ strokeColor: "#ff4570", strokeWeight: 5 }} />
                <Marker
                  position={jeepneySegmentB[jeepneySegmentB.length - 1]}
                  label={{ text: "E", fontSize: "1.5em", fontWeight: "bold", color: "#ffffff" }}
                />
              </>
            )}

            {/* Final Walk */}
            {walkFromJeepney && (
              <>
                <DirectionsRenderer
                  directions={walkFromJeepney}
                  options={{
                    polylineOptions: { strokeColor: "#093843", strokeWeight: 3 }, suppressMarkers: true,
                    preserveViewport: true
                  }}
                />
                <Marker
                  position={walkFromJeepney.routes[0].legs[0].end_location}
                  label={{ text: transferWalk ? "F" : "D", fontSize: "1.5em", fontWeight: "bold", color: "#ffffff" }}
                />
              </>
            )}
          </GoogleMap>
          </div>
          </>
        )}
        <MasskaraLoading isOpen={showLoading} backdrop={true} />
      </IonContent>
    </IonPage>
  );
};

export default Explore;
